import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button } from 'base/ui/button';
import { IconAdd, IconBrand, IconEdit } from 'base/ui/icons';
import { ToastQueue } from 'base/ui/toast';
import { BlocksService } from 'blocks/blocks_service';
import { currentUrl } from 'blocks/current_url';
import { SitemapAddPanel } from 'blocks/editor/add_panel/sitemap';
import { WireframeAddPanel } from 'blocks/editor/add_panel/wireframe';
import { Assistant } from 'blocks/editor/ai/assistant';
import { CommentingExport } from 'blocks/editor/commenting';
import { ProjectEditingActions } from 'blocks/editor/editing_actions/project';
import {
  EditorMode,
  editorModeStore,
  isWireframeOrDesign,
} from 'blocks/editor/editor_mode';
import { EditPanel } from 'blocks/editor/edit_panel';
import { ReplaceComponentPanel } from 'blocks/editor/edit_panel/wireframe_replace';
import { SelectionStore } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { spaceIsEmpty } from '../ai/page_sections';
import { DesignStyleGuidePanel } from '../design/style_guide_panel';
import { DesignImagesPanel } from '../edit_panel/design_images';
import { DesignSchemesPanel } from '../edit_panel/design_schemes';
import { EXPRIMENT_QUERY_PARAM } from '../experiment_prompt';
import styles from './index.css';
import { CommonPanelProps } from './panel_props';
import { SidebarNavigation } from './routes';

type SidebarProps = {
  editing: ProjectEditingActions | undefined;
  selectionActions: SelectionActions;
};

export const SIDEBAR_WIDTH_PX = parseFloat(styles.sidebarWidth);

export function createSidebar(
  assistant: Assistant,
  analytics: AnalyticsService,
  blocksService: BlocksService,
  library: LibraryCache,
  toastQueue: ToastQueue,
  selection: SelectionStore,
  session: ProjectSession,
  commenting: Pick<CommentingExport, 'getSidebarPanel'>,
  flags: FeatureFlagsStore,
) {
  const navigation = new SidebarNavigation();
  // Initialize the navigation state
  performOnLoadAction(navigation, session, selection);

  function getPanel(
    editing: ProjectEditingActions | undefined,
    selectionActions: SelectionActions,
  ) {
    const commentingPanel = commenting.getSidebarPanel();
    if (commentingPanel) {
      return { panel: commentingPanel, isFullHeight: true, hideButtons: true };
    }

    const { at } = navigation;
    if (!editing || !at) return;
    const commonPanelProps: CommonPanelProps = {
      session,
      blocksService,
      toastQueue,
      flags,
      editing,
      selectionActions,
      assistant,
      analytics,
      library,
      selection,
      navigation,
    };
    if (at.path === 'edit') {
      // Don't show panel when the user can't view the wireframe
      const sel = selection.get();
      if (
        isWireframeOrDesign(editorModeStore.get()) &&
        sel.page != null &&
        !session.canViewWireframe(sel.page)
      ) {
        return null;
      }

      return {
        panel: <EditPanel {...commonPanelProps} />,
      };
    }
    if (at.path === 'edit/replace-component') {
      return { panel: <ReplaceComponentPanel {...commonPanelProps} /> };
    }
    if (at.path === 'edit/schemes') {
      if (editorModeStore.get() !== EditorMode.Design) {
        return null;
      }
      return { panel: <DesignSchemesPanel {...commonPanelProps} /> };
    }
    if (at.path === 'edit/images') {
      if (editorModeStore.get() !== EditorMode.Design) {
        return null;
      }
      return { panel: <DesignImagesPanel {...commonPanelProps} /> };
    }
    if (
      at.path === 'add' ||
      at.path === 'add/page-categories' ||
      at.path === 'add/search'
    ) {
      if (editorModeStore.get() === EditorMode.Sitemap) {
        return {
          panel: <SitemapAddPanel {...commonPanelProps} route={at} />,
          isFullHeight: true,
        };
      } else {
        return {
          panel: <WireframeAddPanel {...commonPanelProps} route={at} />,
          isFullHeight: selection.get().element === undefined,
        };
      }
    }
    if (at.path === 'styleguide') {
      return {
        panel: <DesignStyleGuidePanel {...commonPanelProps} />,
        isFullHeight: true,
      };
    }
    throw new UnreachableError(at);
  }

  const CurrentPanel = observer(function _CurrentPanel({
    editing,
    selectionActions,
  }: SidebarProps) {
    const panel = getPanel(editing, selectionActions);

    return (
      <div
        className={classNames(
          styles.panel,
          panel?.isFullHeight && styles.isFullHeight,
          panel?.hideButtons && styles.hideButtons,
          styles.positionLeft,
          (panel == null || editorModeStore.get() === EditorMode.Styleguide) &&
            styles.hidden,
        )}
      >
        {panel?.panel ?? null}
      </div>
    );
  });
  const Sidebar = (props: SidebarProps) => {
    return (
      <>
        {(props.editing || session.locked) && (
          <Buttons navigation={navigation} locked={session.locked} />
        )}
        <CurrentPanel {...props} />
      </>
    );
  };

  return {
    Sidebar,
    navigation,
  };
}

function performOnLoadAction(
  navigation: SidebarNavigation,
  session: ProjectSession,
  selection: SelectionStore,
) {
  // If the project is new, set the selection to the home space and navigate to the edit page
  //   This open the edit panel by default
  const { project } = selection;
  const spaceEmpty = spaceIsEmpty(project, selection.get().space || 'home');
  let showExperiment = currentUrl.searchParams.has(EXPRIMENT_QUERY_PARAM);

  if (showExperiment && !spaceEmpty) {
    showExperiment = false;
  }

  if (session.isNewProject) {
    selection.set({ space: 'home' });
    // If the URL doesn't have an exp query parameter, navigate to the edit page
    //   This check is part of the experiment 6 EXP6 from growth team
    if (!showExperiment) {
      navigation.goto({ path: 'edit' });
    }
    return;
  }
}

const Buttons = observer(function _Buttons({
  locked,
  navigation,
}: {
  locked: boolean;
  navigation: SidebarNavigation;
}) {
  const onClickEdit = React.useCallback(() => {
    navigation.toggleToplevel('edit');
  }, [navigation]);

  const onClickAdd = React.useCallback(() => {
    navigation.toggleToplevel('add');
  }, [navigation]);

  const onClickStyleGuide = React.useCallback(() => {
    navigation.toggleToplevel('styleguide');
  }, [navigation]);

  useKeyboardShortcuts(!locked, {
    // Alt-8 is what Figma uses for "open design panel":
    [shortcut('8', { alt: true })]: onClickEdit,
    [shortcut('a', { alt: true })]: onClickAdd,
  });
  const classes = classNames(styles.buttons, styles.positionLeft);
  return (
    <div className={classes}>
      <Button
        icon={IconEdit}
        tooltip={
          locked
            ? 'This project is view only'
            : navigation.toplevel !== 'edit'
            ? 'Edit'
            : undefined
        }
        tooltipShortcut={locked ? undefined : '⌥8'}
        tooltipPlacement="horizontal"
        checked={navigation.toplevel === 'edit'}
        onClick={onClickEdit}
        size="large"
        disabled={locked}
      />
      <Button
        icon={IconAdd}
        tooltip={
          locked
            ? 'This project is view only'
            : navigation.toplevel !== 'add'
            ? 'Add section or page'
            : undefined
        }
        tooltipShortcut={locked ? undefined : '⌥A'}
        tooltipPlacement="horizontal"
        checked={navigation.toplevel === 'add'}
        onClick={onClickAdd}
        size="large"
        disabled={locked}
      />
      {editorModeStore.get() === EditorMode.Design && (
        <Button
          icon={IconBrand}
          tooltip="Style Guide"
          tooltipPlacement="horizontal"
          checked={navigation.toplevel === 'styleguide'}
          onClick={onClickStyleGuide}
          size="large"
        />
      )}
    </div>
  );
});
